import { useRef, useState, useMemo, useEffect } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { CalendarDays, Edit, Loader2, Mail, Phone, Trash2, User, Building2, UserMinus, Camera, Upload, FileText, Download } from "lucide-react"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"

import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { APP_NAME } from "@/lib/appConfig"
import { useTenant, useUpdateTenant, useDeleteTenant } from "@/features/tenants/hooks/useTenants"
import { useUnassignLease, useCreateLease } from "@/features/leases/hooks/useLeases"
import { useProperties } from "@/features/properties/hooks/useProperties"
import { TenantForm } from "@/features/tenants/components/TenantForm"
import { tenantsService } from "@/features/tenants/services/tenantsService"
import { PageHeader } from "@/components/shared/PageHeader"
import { StatusBadge } from "@/components/shared/StatusBadge"
import { ListSkeleton } from "@/components/shared/LoadingSkeleton"
import { ConfirmDialog } from "@/components/shared/ConfirmDialog"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useCurrency } from "@/hooks/useCurrency"
import { useQuery } from "@tanstack/react-query"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CURRENCIES } from "@/lib/constants"

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function formatFileSize(kb: number) {
  if (kb < 1024) return `${kb} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

const CATEGORY_LABELS: Record<string, string> = {
  id_document: "ID Document",
  right_to_rent: "Right to Rent",
  employment_proof: "Employment Proof",
  reference: "Reference",
  other: "Other",
}

const DOCUMENT_CATEGORIES = [
  { value: "id_document", label: "ID Document" },
  { value: "right_to_rent", label: "Right to Rent" },
  { value: "employment_proof", label: "Employment Proof" },
  { value: "reference", label: "Reference" },
  { value: "other", label: "Other" },
]

const assignToPropertySchema = z.object({
  property_id: z.string().min(1, "Property is required"),
  start_date: z.string().min(1, "Start date is required"),
  end_date: z.string().optional(),
  monthly_rent: z.coerce.number().min(0),
  currency: z.string().min(1),
  deposit: z.coerce.number().optional(),
})

type AssignToPropertyFormValues = z.infer<typeof assignToPropertySchema>

function AssignToPropertySheet({
  open,
  onOpenChange,
  tenantId,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  tenantId: string
}) {
  const createLease = useCreateLease()
  const { data: properties = [] } = useProperties()

  const form = useForm<AssignToPropertyFormValues>({
    resolver: zodResolver(assignToPropertySchema),
    defaultValues: {
      property_id: "",
      start_date: "",
      end_date: "",
      monthly_rent: 0,
      currency: "EUR",
      deposit: undefined,
    },
  })

  function onSubmit(values: AssignToPropertyFormValues) {
    const payload = Object.fromEntries(
      Object.entries({ ...values, tenant_id: tenantId }).filter(
        ([, v]) => v !== "" && v !== undefined
      )
    )
    createLease.mutate(payload, {
      onSuccess: () => {
        toast.success("Tenant assigned to property")
        form.reset()
        onOpenChange(false)
      },
      onError: (err: unknown) => {
        toast.error(err instanceof Error ? err.message : "Failed to assign to property")
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>Assign to Property</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
            <FormField
              control={form.control}
              name="property_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Property *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="monthly_rent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monthly Rent *</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Currency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="deposit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Deposit</FormLabel>
                  <FormControl>
                    <Input type="number" min={0} placeholder="0" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full h-12 rounded-xl"
              disabled={createLease.isPending}
            >
              {createLease.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                "Assign to Property"
              )}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { data: tenant, isLoading, isError } = useTenant(id ?? "")
  const deleteTenant = useDeleteTenant()
  const unassignLease = useUnassignLease()
  const navigate = useNavigate()
  const { format } = useCurrency()

  const [editOpen, setEditOpen] = useState(false)
  const [assignPropertyOpen, setAssignPropertyOpen] = useState(false)
  const [unassignLeaseId, setUnassignLeaseId] = useState<string | null>(null)

  // Auto-open edit sheet if ?edit=true is in URL
  useState(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.get("edit") === "true") {
        setEditOpen(true)
        window.history.replaceState({}, "", window.location.pathname)
      }
    }
  })

  useEffect(() => {
    if (!tenant) return
    const prev = document.title
    document.title = `${tenant.full_name} | ${APP_NAME}`
    return () => { document.title = prev }
  }, [tenant?.full_name])

  // Documents
  const { data: documents = [], refetch: refetchDocs } = useQuery({
    queryKey: ["tenant-documents", id],
    queryFn: () => tenantsService.listDocuments(id!),
    enabled: !!id,
  })

  // Photo state
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [deletingPhoto, setDeletingPhoto] = useState(false)
  const photoInputRef = useRef<HTMLInputElement>(null)

  // Document upload state
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [docCategory, setDocCategory] = useState("other")
  const docInputRef = useRef<HTMLInputElement>(null)

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !id) return
    setUploadingPhoto(true)
    try {
      await tenantsService.uploadPhoto(id, file)
      await queryClient.invalidateQueries({ queryKey: ["tenants", id] })
      toast.success("Photo updated")
    } catch {
      toast.error("Failed to upload photo")
    }
    setUploadingPhoto(false)
    e.target.value = ""
  }

  async function handlePhotoDelete() {
    if (!id) return
    setDeletingPhoto(true)
    try {
      await tenantsService.deletePhoto(id)
      await queryClient.invalidateQueries({ queryKey: ["tenants", id] })
      toast.success("Photo removed")
    } catch {
      toast.error("Failed to remove photo")
    }
    setDeletingPhoto(false)
  }

  async function handleDocUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !id) return
    setUploadingDoc(true)
    try {
      await tenantsService.uploadDocument(id, file, docCategory)
      await refetchDocs()
      queryClient.invalidateQueries({ queryKey: ["plan", "usage"] })
      toast.success("Document uploaded")
    } catch {
      toast.error("Failed to upload document")
    }
    setUploadingDoc(false)
    e.target.value = ""
  }

  async function handleDocDelete(docId: string) {
    try {
      await tenantsService.deleteDocument(docId)
      await refetchDocs()
      queryClient.invalidateQueries({ queryKey: ["plan", "usage"] })
      toast.success("Document deleted")
    } catch {
      toast.error("Failed to delete document")
    }
  }

  if (isLoading) return <ListSkeleton count={4} />

  if (isError || !tenant) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Tenant" back />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">Tenant not found.</p>
        </div>
      </div>
    )
  }

  const t = tenant as unknown as {
    photo_url?: string | null
    phone?: string
    nationality?: string
    employment_status?: string
    monthly_income?: number
    income_currency?: string
    emergency_contact_name?: string
    emergency_contact_phone?: string
    notes?: string
    status?: string
    leases?: Array<{
      id: string
      status?: string
      start_date?: string
      end_date?: string
      monthly_rent?: number
      property?: { id?: string; name?: string }
    }>
  }

  const activeLeases = t.leases?.filter((l) => l.status === "active") ?? []

  return (
    <div className="flex flex-col h-full">
      <PageHeader title={tenant.full_name} back />

      <div className="flex-1 overflow-y-auto pb-nav bg-background relative">
        {/* Hero Avatar section */}
        <div className="relative w-full pt-10 pb-6 flex flex-col items-center justify-center bg-gradient-to-b from-primary/5 via-primary/5 to-background">
          <div className="relative mb-5">
            <div className={`rounded - full p - [4px] transition - all duration - 500 shadow - soft ${activeLeases.length > 0 ? 'bg-gradient-to-tr from-brand via-primary to-indigo-500 shadow-primary/20' : 'bg-border/50'} `}>
              <div className="w-28 h-28 rounded-full bg-background flex items-center justify-center overflow-hidden border-4 border-background relative">
                {t.photo_url ? (
                  <img src={t.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold text-primary">
                    {getInitials(tenant.full_name)}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute bottom-1 right-1 w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:scale-105 transition-transform active:scale-95"
            >
              <Camera className="h-4 w-4" />
            </button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoChange}
            />
          </div>

          <div className="text-center px-4">
            <h2 className="font-extrabold text-2xl tracking-tight mb-2">{tenant.full_name}</h2>
            <div className="flex items-center justify-center gap-2 mb-4">
              <StatusBadge status={t.status ?? "active"} />
              <Button variant="outline" size="sm" className="h-8 rounded-xl gap-1 text-xs" onClick={() => setEditOpen(true)}>
                <Edit className="h-3.5 w-3.5" />
                Edit
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground font-medium">
              {tenant.email && (
                <div className="flex items-center gap-1.5">
                  <Mail className="h-4 w-4 text-primary/60" />
                  {tenant.email}
                </div>
              )}
              {t.phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-primary/60" />
                  {t.phone}
                </div>
              )}
            </div>

            {t.photo_url && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-4 h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 rounded-full px-4"
                onClick={handlePhotoDelete}
                disabled={deletingPhoto}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Remove photo
              </Button>
            )}
          </div>
        </div>

        {/* Assign to property action */}
        <div className="px-4 pb-2">
          <Button
            variant="outline"
            className="w-full h-11 rounded-2xl border-dashed border-primary/40 text-primary hover:bg-primary/5 hover:border-primary/60 transition-all"
            onClick={() => setAssignPropertyOpen(true)}
          >
            <Building2 className="h-4 w-4 mr-2" />
            Assign to Property
          </Button>
        </div>

        {/* Current property */}
        {activeLeases.length > 0 && (
          <div className="px-4 pb-4">
            <p className="text-xs font-bold text-primary/80 uppercase tracking-widest mb-3 ml-1">
              Active Property
            </p>
            <div className="space-y-2">
              {activeLeases.map((lease) => (
                <Link key={lease.id} to={`/properties/${lease.property?.id}`}>
                  <Card className="rounded-2xl border-border/50 bg-card/60 backdrop-blur-xl shadow-soft hover:shadow-md transition-all group overflow-hidden">
                    <CardContent className="p-3.5 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <span className="text-sm font-semibold group-hover:text-primary transition-colors">
                          {lease.property?.name ?? "Unknown property"}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setUnassignLeaseId(lease.id);
                        }}
                        disabled={unassignLease.isPending}
                      >
                        {unassignLease.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserMinus className="h-3.5 w-3.5 mr-1.5" />}
                        Unassign
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="px-4 relative z-20 space-y-4">
          <Tabs defaultValue="leases">
            <TabsList className="w-full h-12 rounded-2xl mb-4 bg-muted/50 backdrop-blur-xl p-1 relative grid grid-cols-3">
              <TabsTrigger value="leases" className="rounded-xl text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm">
                Leases
              </TabsTrigger>
              <TabsTrigger value="documents" className="rounded-xl text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm">
                Docs {documents.length > 0 && `(${documents.length})`}
              </TabsTrigger>
              <TabsTrigger value="details" className="rounded-xl text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm">
                Details
              </TabsTrigger>
            </TabsList>

            <TabsContent value="leases" className="animate-in fade-in-50 duration-300 outline-none">
              {!t.leases?.length ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No leases for this tenant.
                </p>
              ) : (
                <div className="space-y-3">
                  {t.leases.map((lease) => (
                    <Card key={lease.id} className="rounded-3xl border-border/50 bg-card/60 backdrop-blur-md shadow-soft">
                      <CardContent className="p-5">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-base mb-1 tracking-tight">
                              {lease.property?.name ?? "Unknown property"}
                            </p>
                            <p className="text-xs text-muted-foreground font-medium">
                              {formatDate(lease.start_date)} — {formatDate(lease.end_date)}
                            </p>
                          </div>
                          <div className="text-right flex flex-col items-end gap-1.5">
                            <StatusBadge status={lease.status ?? "active"} />
                            {lease.monthly_rent != null && (
                              <span className="text-sm font-bold text-foreground bg-primary/5 px-2.5 py-1 rounded-lg">
                                {format(lease.monthly_rent)}<span className="text-muted-foreground font-normal text-xs">/mo</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="documents" className="animate-in fade-in-50 duration-300 outline-none">
              {/* Upload row */}
              <div className="flex gap-2 mb-4">
                <select
                  value={docCategory}
                  onChange={(e) => setDocCategory(e.target.value)}
                  className="flex-1 h-11 rounded-xl border border-border/50 bg-card/60 backdrop-blur-md px-3 text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all shadow-sm"
                >
                  {DOCUMENT_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <Button
                  variant="default"
                  className="h-11 rounded-xl px-4 shadow-soft hover:shadow-glow transition-all active:scale-[0.98]"
                  onClick={() => docInputRef.current?.click()}
                  disabled={uploadingDoc}
                >
                  <Upload className="h-4 w-4 mr-1.5" />
                  {uploadingDoc ? "Uploading..." : "Upload"}
                </Button>
                <input
                  ref={docInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleDocUpload}
                />
              </div>

              {documents.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No documents yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <Card key={doc.id} className="rounded-2xl border-border/50 bg-card/60 backdrop-blur-md shadow-soft hover:shadow-md transition-shadow group">
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <FileText className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold truncate tracking-tight">{doc.file_name}</p>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              {doc.category && (
                                <Badge variant="secondary" className="text-[10px] uppercase font-bold tracking-wider h-5 px-2 rounded-md bg-secondary/50">
                                  {CATEGORY_LABELS[doc.category] ?? doc.category}
                                </Badge>
                              )}
                              <span className="text-xs font-medium text-muted-foreground">
                                {formatFileSize(doc.file_size_kb)} · {formatDate(doc.uploaded_at)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-auto mt-2 sm:mt-0">
                            <a
                              href={doc.file_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-lg hover:bg-primary/10 hover:text-primary transition-colors bg-background/50"
                            >
                              <Download className="h-4 w-4" />
                            </a>
                            <button
                              type="button"
                              onClick={() => handleDocDelete(doc.id)}
                              className="inline-flex items-center justify-center h-8 w-8 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors bg-background/50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="details" className="animate-in fade-in-50 duration-300 outline-none">
              <Card className="rounded-3xl border-border/50 bg-card/60 backdrop-blur-md shadow-soft">
                <CardContent className="p-6 space-y-4">
                  {[
                    { label: "Nationality", value: t.nationality },
                    { label: "Employment", value: t.employment_status },
                    {
                      label: "Monthly Income",
                      value:
                        t.monthly_income != null
                          ? `${t.income_currency ?? ""} ${t.monthly_income} `
                          : null,
                    },
                    { label: "Emergency Contact", value: t.emergency_contact_name },
                    { label: "Emergency Phone", value: t.emergency_contact_phone },
                    { label: "Notes", value: t.notes },
                  ]
                    .filter((r) => r.value)
                    .map((row) => (
                      <div key={row.label} className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground font-medium uppercase tracking-wider text-xs">{row.label}</span>
                        <span className="font-semibold text-right max-w-[60%] text-foreground">
                          {row.value}
                        </span>
                      </div>
                    ))}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {tenant && (
          <TenantForm
            open={editOpen}
            onOpenChange={setEditOpen}
            onSuccess={() => setEditOpen(false)}
            tenant={tenant}
          />
        )}
        <AssignToPropertySheet
          open={assignPropertyOpen}
          onOpenChange={setAssignPropertyOpen}
          tenantId={id ?? ""}
        />
      </div>
      <ConfirmDialog
        open={!!unassignLeaseId}
        onOpenChange={(open) => {
          if (!open) setUnassignLeaseId(null)
        }}
        title="Unassign Tenant"
        description="Are you sure you want to unassign this tenant from the property? The lease will drop to a terminated state starting today."
        confirmLabel="Unassign"
        onConfirm={() => {
          if (unassignLeaseId) {
            unassignLease.mutate(unassignLeaseId, {
              onSuccess: () => {
                toast.success("Tenant unassigned")
                setUnassignLeaseId(null)
              },
              onError: (err: any) => {
                toast.error(err.response?.data?.error || "Failed to unassign")
                setUnassignLeaseId(null)
              }
            })
          }
        }}
      />
    </div>
  )
}
